/**
 * APC Price Research — dashboard y orquestador del módulo de monitoreo de
 * precios. Toma productos del inventario existente (no duplica), permite
 * activarlos para investigación y muestra recomendaciones.
 *
 * Fuentes V1:
 *   - mercadolibre (automática): bloqueada hasta que el admin configure OAuth.
 *   - manual: el reviewer pega listings comparables; el motor los analiza igual.
 *
 * NUNCA cambia precios automáticamente — toda recomendación es informativa.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChartLineUp, MagnifyingGlass, Play, Plus, Trash, ArrowSquareOut, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_LABEL = {
  competitivo: { label: "Competitivo", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  ligeramente_alto: { label: "Ligeramente alto", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  alto: { label: "Alto", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  muy_alto: { label: "Muy alto", cls: "bg-red-100 text-red-800 border-red-300" },
  debajo: { label: "Debajo del mercado", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  no_data: { label: "Sin datos", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

const fmtMx = (v, c = "MXN") => new Intl.NumberFormat("es-MX", { style: "currency", currency: c }).format(Number(v || 0));

export default function PriceResearch() {
  const [tab, setTab] = useState("dashboard"); // dashboard | products | jobs
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [manualFor, setManualFor] = useState(null);
  const [detailJob, setDetailJob] = useState(null);

  const loadProducts = async () => {
    const { data } = await api.get("/research/products");
    setProducts(data);
  };
  const loadSummary = async () => {
    try { const { data } = await api.get("/research/summary"); setSummary(data); } catch { setSummary(null); }
  };
  const loadJobs = async () => {
    try { const { data } = await api.get("/research/jobs?limit=50"); setJobs(data); } catch { setJobs([]); }
  };
  useEffect(() => {
    loadProducts(); loadSummary(); loadJobs();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
  }, [products, search]);

  const toggle = async (p) => {
    try {
      await api.patch(`/research/products/${p.id}`, { enabled: !p.research_enabled });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, research_enabled: !p.research_enabled } : x)));
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al actualizar"); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">INTELIGENCIA COMERCIAL · V1</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <ChartLineUp size={32} weight="duotone" className="text-[#002FA7]" />
            APC Price Research
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">Monitorea cómo están tus precios contra el mercado y recibe recomendaciones inteligentes. Tú decides si actualizas o no.</p>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap">
        {[
          { v: "dashboard", label: "Dashboard", testid: "research-tab-dashboard" },
          { v: "products", label: "Productos monitoreados", testid: "research-tab-products" },
          { v: "jobs", label: "Histórico", testid: "research-tab-jobs" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            data-testid={t.testid}
            className={`px-4 py-2 rounded-sm border text-sm font-semibold transition ${tab === t.v ? "bg-[#002FA7] border-[#002FA7] text-white" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <DashboardView summary={summary} onOpenProduct={(pid) => { const p = products.find((x) => x.id === pid); if (p) setManualFor(p); }} />
      )}

      {tab === "products" && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto…" className="pl-9" />
          </div>
          <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-3">Producto</th>
                  <th className="text-right px-4 py-3">Precio APC</th>
                  <th className="text-right px-4 py-3">Precio mercado</th>
                  <th className="text-right px-4 py-3">Diferencia</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-center px-4 py-3">Monitor</th>
                  <th className="text-right px-4 py-3 w-48">Acciones</th>
                </tr>
              </thead>
              <tbody data-testid="research-products-body">
                {filtered.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-zinc-400">Sin productos.</td></tr>}
                {filtered.map((p) => {
                  const rec = p.last_recommendation;
                  const st = rec?.status || "no_data";
                  const tag = STATUS_LABEL[st];
                  return (
                    <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`research-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {(p.photos || [])[0] && <img src={p.photos[0]} alt="" className="w-9 h-9 object-cover rounded border border-zinc-200" />}
                          <div>
                            <div className="font-medium leading-tight">{p.name}</div>
                            <div className="text-[10px] uppercase text-zinc-400 font-mono">{p.brand}{p.sku ? ` · ${p.sku}` : ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right numeric font-semibold">{fmtMx(p.store_price, p.sale_currency)}</td>
                      <td className="px-4 py-3 text-right numeric text-zinc-700">{rec && rec.market_median ? fmtMx(rec.market_median, rec.apc_currency) : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {rec && rec.status !== "no_data" ? (
                          <span className={`font-semibold ${rec.deviation_pct > 5 ? "text-red-600" : rec.deviation_pct < -5 ? "text-blue-600" : "text-emerald-600"}`}>
                            {rec.deviation_pct > 0 ? "+" : ""}{rec.deviation_pct}%
                          </span>
                        ) : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded ${tag.cls}`}>{tag.label}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggle(p)} data-testid={`toggle-${p.id}`}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${p.research_enabled ? "bg-emerald-500" : "bg-zinc-300"}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${p.research_enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" onClick={() => setManualFor(p)} className="gap-1 text-xs bg-[#002FA7] hover:bg-[#001a6a] text-white" data-testid={`manual-${p.id}`}>
                            <Plus size={13}/>Analizar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "jobs" && (
        <div className="bg-white border border-zinc-200 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Producto</th>
                <th className="text-left px-4 py-3">Fuentes</th>
                <th className="text-right px-4 py-3">Resultados</th>
                <th className="text-right px-4 py-3">Válidos</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody data-testid="research-jobs-body">
              {jobs.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-zinc-400">Aún no se ha ejecutado ninguna investigación.</td></tr>}
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium">{j.product_name}</td>
                  <td className="px-4 py-3 text-xs uppercase text-zinc-500 font-mono">{(j.sources || []).join(", ")}</td>
                  <td className="px-4 py-3 text-right numeric">{j.result_count ?? 0}</td>
                  <td className="px-4 py-3 text-right numeric font-semibold">{j.valid_count ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${j.status === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{new Date(j.started_at).toLocaleString("es-MX")}</td>
                  <td className="px-2"><button onClick={() => setDetailJob(j)} className="p-1.5 hover:bg-[#002FA7]/10 rounded text-[#002FA7]"><ArrowSquareOut size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ManualResearchDialog
        product={manualFor}
        onClose={() => setManualFor(null)}
        onDone={() => { loadProducts(); loadSummary(); loadJobs(); }}
      />
      <JobDetailDialog job={detailJob} onClose={() => setDetailJob(null)} />
    </div>
  );
}

function DashboardStat({ label, value, color }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className="mt-1 font-heading text-3xl font-extrabold tracking-tight numeric" style={{ color }}>{value}</div>
    </div>
  );
}

function DashboardView({ summary, onOpenProduct }) {
  if (!summary) return <div className="p-10 text-center text-zinc-400">Cargando dashboard…</div>;
  const counts = summary.counts || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="research-summary-cards">
        <DashboardStat label="Productos monitoreados" value={summary.monitored} color="#002FA7" />
        <DashboardStat label="Competitivos" value={counts.competitivo || 0} color="#00873D" />
        <DashboardStat label="Arriba del mercado" value={(counts.alto || 0) + (counts.muy_alto || 0) + (counts.ligeramente_alto || 0)} color="#D17A00" />
        <DashboardStat label="Debajo del mercado" value={counts.debajo || 0} color="#0066CC" />
      </div>
      <div className="bg-white border border-zinc-200 rounded-md">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
          <Warning size={18} weight="duotone" className="text-amber-600" />
          <h3 className="font-semibold">Alertas activas <span className="text-zinc-500 font-mono text-xs">{summary.alerts_total}</span></h3>
        </div>
        {(summary.alerts || []).length === 0 ? (
          <div className="p-8 text-center text-zinc-400 text-sm">Sin alertas. Todo dentro del rango competitivo. ✨</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {summary.alerts.map((a) => {
              const tag = STATUS_LABEL[a.status];
              return (
                <li key={a.product_id} className="p-3 flex items-center gap-3 hover:bg-zinc-50">
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border rounded ${tag.cls}`}>{tag.label}</span>
                  <span className="flex-1 truncate font-medium">{a.name}</span>
                  <span className="text-sm text-zinc-700"><span className="font-mono">{fmtMx(a.apc_price)}</span> · <span className={a.deviation_pct > 0 ? "text-red-600" : "text-emerald-600"}>{a.deviation_pct > 0 ? "+" : ""}{a.deviation_pct}%</span></span>
                  <span className="text-sm text-emerald-700 font-semibold">→ {fmtMx(a.recommended_price)}</span>
                  <button onClick={() => onOpenProduct(a.product_id)} className="text-[#002FA7] hover:bg-[#002FA7]/10 p-1 rounded"><ArrowSquareOut size={14}/></button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ManualResearchDialog({ product, onClose, onDone }) {
  const [items, setItems] = useState([{ title: "", price: "", url: "", condition: "used" }]);
  const [submitting, setSubmitting] = useState(false);
  const [importingIdx, setImportingIdx] = useState(-1);

  useEffect(() => {
    if (product) setItems([{ title: "", price: "", url: "", condition: "used" }]);
  }, [product]);

  const setItem = (i, k, v) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addRow = () => setItems((prev) => [...prev, { title: "", price: "", url: "", condition: "used" }]);
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const importUrl = async (i) => {
    const url = items[i].url?.trim();
    if (!url || !/^https?:\/\//.test(url)) {
      toast.error("Pega una URL válida en este renglón primero.");
      return;
    }
    setImportingIdx(i);
    try {
      const { data } = await api.post("/research/extract-url", { url });
      setItems((prev) => prev.map((it, idx) => idx === i ? {
        ...it,
        title: data.title || it.title,
        price: data.price ? String(data.price) : it.price,
        condition: data.condition === "used" ? "used" : (data.condition || it.condition || "used"),
      } : it));
      if (data.price) {
        toast.success("Listing importado completo ✓ (precio + título + condición)");
      } else if (data.title) {
        toast.info("Título importado. Captura el precio manualmente del listing.", { duration: 5500 });
      } else {
        toast.warning("No se pudo extraer nada. Captura título y precio manualmente.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo importar la URL");
    } finally { setImportingIdx(-1); }
  };

  const submit = async () => {
    const clean = items.filter((x) => x.title.trim().length >= 2 && Number(x.price) > 0);
    if (clean.length === 0) { toast.error("Agrega al menos un comparable válido (título + precio)."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/research/jobs/manual", {
        product_id: product.id,
        items: clean.map((x) => ({ title: x.title.trim(), price: Number(x.price), url: x.url.trim(), condition: x.condition || "" })),
      });
      const st = STATUS_LABEL[data.recommendation_status]?.label || data.recommendation_status;
      toast.success(`Análisis listo · ${data.valid_count} comparables válidos · ${st}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error procesando comparables");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Plus size={22} weight="duotone" className="text-[#002FA7]" />
            Comparables manuales
          </DialogTitle>
        </DialogHeader>
        {product && (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 rounded p-3 text-sm">
              <span className="font-semibold">{product.name}</span>
              <span className="text-zinc-500"> · APC {fmtMx(product.store_price, product.sale_currency)}</span>
            </div>
            <p className="text-xs text-zinc-500">Pega los listings comparables que encontraste en OfferUp, FB Marketplace, Amazon u otro sitio. <b>Tip:</b> pega solo la URL y dale <b>Importar</b> para autocompletar título y (cuando se pueda) precio.</p>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`manual-row-${i}`}>
                  <Input value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} placeholder="Título del listing" className="col-span-4 text-sm" />
                  <Input type="number" step="0.01" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="Precio" className="col-span-2 text-sm numeric" />
                  <select value={it.condition} onChange={(e) => setItem(i, "condition", e.target.value)} className="col-span-1 h-9 px-1 text-xs border border-zinc-300 rounded-md bg-white">
                    <option value="new">Nuevo</option>
                    <option value="used">Usado</option>
                  </select>
                  <Input value={it.url} onChange={(e) => setItem(i, "url", e.target.value)} placeholder="https://… URL" className="col-span-3 text-sm" />
                  <button
                    onClick={() => importUrl(i)}
                    disabled={importingIdx === i || !it.url}
                    className="col-span-1 h-9 px-1.5 text-[10px] uppercase font-bold border border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/10 rounded-md disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    title="Importar título y precio desde la URL"
                    data-testid={`manual-import-${i}`}
                  >
                    {importingIdx === i ? "…" : "Importar"}
                  </button>
                  <button onClick={() => remove(i)} className="col-span-1 p-2 text-red-600 hover:bg-red-50 rounded" disabled={items.length === 1}>
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" onClick={addRow} className="gap-1"><Plus size={14}/> Agregar otro</Button>
            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={submit} disabled={submitting} className="bg-[#002FA7] hover:bg-[#001a6a] text-white gap-1" data-testid="manual-submit">
                {submitting ? "Analizando…" : <><Play size={14}/> Analizar mercado</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function JobDetailDialog({ job, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!job) { setData(null); return; }
    api.get(`/research/jobs/${job.id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [job]);
  const rec = data?.recommendation;
  const results = data?.results || [];
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ChartLineUp size={20} className="text-[#002FA7]" /> {job?.product_name}
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="py-12 text-center text-zinc-400">Cargando…</div> : (
          <div className="space-y-4">
            {rec && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label="APC" value={fmtMx(rec.apc_price, rec.apc_currency)} />
                <Stat label="Mediana" value={fmtMx(rec.market_median)} />
                <Stat label="Promedio" value={fmtMx(rec.market_avg)} />
                <Stat label="Rango" value={`${fmtMx(rec.market_min)} – ${fmtMx(rec.market_max)}`} />
                <Stat label="Estado" value={STATUS_LABEL[rec.status]?.label || rec.status} highlight />
              </div>
            )}
            {rec?.recommended_price && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm flex items-center gap-2">
                <CheckCircle size={18} className="text-emerald-700"/>
                <span><b>Recomendación:</b> {rec.rationale} → <b>{fmtMx(rec.recommended_price)}</b></span>
              </div>
            )}
            <div className="border border-zinc-200 rounded overflow-hidden">
              <div className="max-h-[55vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 uppercase text-zinc-500 sticky top-0"><tr>
                    <th className="text-left px-3 py-2">Título</th>
                    <th className="text-right px-3 py-2">Precio</th>
                    <th className="text-center px-3 py-2">Cond.</th>
                    <th className="text-center px-3 py-2">Envío</th>
                    <th className="text-left px-3 py-2">Ubicación</th>
                    <th className="text-center px-3 py-2">Match</th>
                    <th className="text-left px-3 py-2">Fuente</th>
                    <th className="w-7"></th>
                  </tr></thead>
                  <tbody>
                    {results.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-10 text-center text-zinc-400">
                        <div className="space-y-1">
                          <div>Sin resultados todavía para este producto.</div>
                          <div className="text-[10px]">Usa <b>Analizar</b> y pega URLs comparables manualmente.</div>
                        </div>
                      </td></tr>
                    )}
                    {results.map((r) => {
                      const condColor = r.condition === "new" ? "bg-emerald-100 text-emerald-800"
                        : r.condition === "used" || r.condition === "semi-new" ? "bg-amber-100 text-amber-800"
                        : "bg-zinc-100 text-zinc-600";
                      const condLabel = r.condition === "new" ? "Nuevo"
                        : r.condition === "used" ? "Usado"
                        : r.condition === "not_specified" || !r.condition ? "—"
                        : r.condition;
                      return (
                      <tr key={r.id} className={`border-t border-zinc-100 hover:bg-zinc-50/60 ${r.excluded ? "opacity-60" : ""}`}>
                        <td className="px-3 py-2 max-w-xs">
                          {r.url ? (
                            <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-[#002FA7] line-clamp-2">{r.title}</a>
                          ) : <span className="line-clamp-2">{r.title}</span>}
                        </td>
                        <td className="px-3 py-2 text-right numeric font-semibold whitespace-nowrap">{fmtMx(r.price, r.currency)}</td>
                        <td className="px-3 py-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${condColor}`}>{condLabel}</span></td>
                        <td className="px-3 py-2 text-center">
                          {r.free_shipping ? <span className="text-[10px] text-emerald-700 font-semibold">GRATIS</span> : <span className="text-[10px] text-zinc-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-zinc-600 text-[11px] max-w-[150px] truncate">{r.location || "—"}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.match_score >= 80 ? "bg-emerald-100 text-emerald-800" : r.match_score >= 60 ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600"}`}>{r.match_score}%</span>
                          {r.excluded && <div className="text-[9px] text-red-600 mt-0.5" title={r.exclude_reason}>EXCLUIDO</div>}
                        </td>
                        <td className="px-3 py-2 uppercase text-[10px] text-zinc-500 font-mono">{r.source}</td>
                        <td className="px-2">{r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline"><ArrowSquareOut size={12}/></a>}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-2 rounded border ${highlight ? "border-[#002FA7]/30 bg-[#002FA7]/5" : "border-zinc-200 bg-white"}`}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
      <div className={`mt-0.5 font-heading font-extrabold ${highlight ? "text-[#002FA7]" : "text-zinc-900"} text-sm`}>{value}</div>
    </div>
  );
}
